'use client';

import { tv } from 'tailwind-variants';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { EntityRatingCommentCount, Rating } from '@medprev-web-app/api-graphql-client';

import { Icon } from '@/components/ui/icon.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { formatRatingAverageLabel } from '@/utils/formatters/fomat-rating-average-label';
import { AnalyticsEnumKeyType } from '@/types/enums/analytics/analytics.enum';

interface EntityRatingProps {
  rating?: Rating | null;
  commentsCount?: EntityRatingCommentCount | null;
  ['data-analytics-event']?: AnalyticsEnumKeyType;
}

const formatRating = (ratingEntity: Rating): string => {
  const rating = ratingEntity.fiveStars.average;

  if (rating === 0) {
    return 'Sem avaliações';
  }

  const ratingNumber = Number(rating);

  return ratingNumber.toFixed(1).replace('.', ',');
};

const formatCommentCount = (commentCount: EntityRatingCommentCount): string => {
  const count = commentCount.total;

  if (count === 1) {
    return `${count} comentário`;
  }

  return `${count} comentários`;
};

const formatRatingCount = (ratingEntity: Rating): string => {
  const rating = ratingEntity.fiveStars.count;

  if (rating === 1) {
    return `${rating} avaliação`;
  }

  return `${rating} avaliações`;
};

function RatingAverage({ rating }: { rating: Rating }) {
  const { average } = rating.fiveStars;

  const averageNumber = Number(average);

  if (averageNumber === 0) {
    return (
      <Typography variant={TypographyVariant.BODY_STRONG} as="span" className="!text-lg !leading-6 sm:!leading-7">
        {formatRating(rating)}
      </Typography>
    );
  }

  if (averageNumber < 2.9) {
    return (
      <Typography variant={TypographyVariant.BODY_STRONG} as="span" className="!text-lg !leading-6 sm:!leading-7">
        {formatRating(rating)}
      </Typography>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Typography variant={TypographyVariant.BODY_STRONG} as="span" className="!text-lg !leading-6 sm:!leading-7" data-testid={DataTestIdEnum.ENTITY_EXCELLENT_RATING}>
        <span data-testid={DataTestIdEnum.ENTITY_AVERAGE_RATING}>{formatRating(rating)}</span>
        {' '}
        •
        {' '}
        {formatRatingAverageLabel(averageNumber)}
      </Typography>
    </div>
  );
}

function RatingCount({ rating, commentCount }: { rating: Rating; commentCount?: EntityRatingCommentCount | null }) {
  if (!commentCount) {
    return null;
  }
  const countRatting = rating.fiveStars.count || 0;
  const countComments = commentCount.total || 0;

  if (countRatting === 0 && countComments === 0) {
    return null;
  }

  if (countRatting > 0 && countComments === 0) {
    return (
      <Typography variant={TypographyVariant.BODY_TEXT} as="span" className="text-green-500" data-testid={DataTestIdEnum.ENTITY_RATING_COUNT}>
        {formatRatingCount(rating)}
      </Typography>
    );
  }

  if (countRatting === 0 && countComments > 0) {
    return (
      <Typography variant={TypographyVariant.BODY_TEXT} as="span" className="text-green-500">
        {formatCommentCount(commentCount)}
      </Typography>
    );
  }

  return (
    <Typography variant={TypographyVariant.BODY_TEXT} as="span" className="text-green-500" data-testid={DataTestIdEnum.ENTITY_FULL_COMMENT_COUNT}>
      {formatRatingCount(rating)}
      {' '}
      e
      {' '}
      {formatCommentCount(commentCount)}
    </Typography>
  );
}

const starStyle = tv({
  slots: {
    star: 'text-warning-400 -mt-1',
  },

  variants: {
    withoutRating: {
      true: {
        star: 'text-gray-400',
      },
    },
  },
});

export function EntityRating({ rating, commentsCount, 'data-analytics-event': analyticsEventName }: EntityRatingProps) {
  const { star } = starStyle();

  function goToRating() {
    const element = document.querySelector('#rating') as HTMLElement;
    const offset = 80;

    if (!element) {
      return;
    }

    window.scrollTo({
      top: element.offsetTop - offset,
      behavior: 'smooth',
    });
  }

  if (!rating?.fiveStars || (rating.fiveStars.count || 0) < 3) {
    return null;
  }

  return (
    <div
      className="py-4 ring-[1.5px] ring-inset ring-green-200 rounded-xxl flex items-center justify-center"
      data-testid={DataTestIdEnum.ENTITY_RATING_CONTAINER}
    >
      <button
        className="text-center flex flex-col gap-1.5"
        data-analytics-event={analyticsEventName}
        onClick={goToRating}
      >
        <div className="flex items-center gap-2 justify-center">
          <Icon icon={faStar} className={star({ withoutRating: rating.fiveStars.count === 0 })} />
          <RatingAverage rating={rating} />
        </div>

        <RatingCount rating={rating} commentCount={commentsCount} />
      </button>
    </div>
  );
}
